// @ts-check

/** @file App-owned adoption of Velocious tenant handles for project replicas. */

import Configuration from "velocious/build/src/configuration.js"
import Tenant from "velocious/build/src/tenants/tenant.js"

import BoardColumn from "../models/board-column.js"
import Board from "../models/board.js"
import Comment from "../models/comment.js"
import ProjectReplicaRegistryEntry from "../models/project-replica-registry-entry.js"
import TaskAssignedUser from "../models/task-assigned-user.js"
import TaskBoard from "../models/task-board.js"
import TaskType from "../models/task-type.js"
import Task from "../models/task.js"
import Timelog from "../models/timelog.js"
import {createAppOfflineMutationLog, registerAppOfflineMutationLog, unregisterAppOfflineMutationLog} from "./app-offline-mutation-log.js"
import {createProjectReplicaMutationLog, syncUuid} from "./project-replica-mutation-log.js"
import {projectReplicaPendingMutationCountForHandle, resetProjectReplicaTransportHandlesForTests} from "./project-replica-runtime.js"
import {projectReplicaSyncDeclaration} from "./project-replica-sync.js"
import {PROJECT_REPLICA_DATABASE_IDENTIFIER, PROJECT_REPLICA_SCHEMA_GENERATION, projectReplicaIdentity} from "./project-replica-contract.js"
import {createProjectSyncCoordinator} from "../sync/project-sync-coordinator.js"
import {bindOfflineSyncProvider, flushOfflineSyncQueue, releaseOfflineSyncProviderBinding} from "../sync/offline-sync-provider.js"
import ProjectSyncClient from "../sync/project-sync-client.js"
import {reportProjectSyncError} from "../shared/project-sync-reporting.js"
import {projectSyncDeviceId} from "../sync/project-sync-identity.js"

export {PROJECT_REPLICA_DATABASE_IDENTIFIER,
  projectReplicaDatabaseName,
  projectReplicaIdentity} from "./project-replica-contract.js"

/** @import TenantHandle from "velocious/build/src/tenants/tenant-handle.js" */
/** @typedef {import("velocious/build/src/sync/local-mutation-log.js").default} LocalMutationLog */
/** @import SyncCoordinator from "velocious/build/src/sync/sync-coordinator.js" */
/** @import {ProjectReplicaIdentity} from "./project-replica-contract.js" */
/** @typedef {import("velocious/build/src/database/migrator/types.js").RequireMigrationContextType} MigrationContext */
/** @typedef {Readonly<{coordinator: SyncCoordinator, handle: TenantHandle, identity: ProjectReplicaIdentity, syncClient: ProjectSyncClient}>} OpenProjectReplica */
/** @typedef {OpenProjectReplica & Readonly<{appOfflineMutationLog?: import("velocious/build/src/sync/local-mutation-log.js").default, migrations: MigrationContext, routingEpoch: number}>} ActiveProjectReplica */

/**
 * Active app clients keyed by the framework's opaque physical database identity.
 * @type {Map<string, ActiveProjectReplica>}
 */
const activeSyncClients = new Map()

/** Serializes only app-owned SyncClient admission/retirement; Velocious still owns every physical handle decision. */
let syncClientLifecycle = Promise.resolve()

/** Error raised when sealing storage would strand unacknowledged intent. */
export class ProjectReplicaUnsyncedError extends Error {
  /**
   * Creates a guarded lifecycle error.
   * @param {number} unsyncedCount Pending/failed mutation count.
   */
  constructor(unsyncedCount) {
    super(`Cannot seal project replicas while ${unsyncedCount} local mutation${unsyncedCount === 1 ? " remains" : "s remain"} unsynced`)
    this.name = "ProjectReplicaUnsyncedError"
    this.unsyncedCount = unsyncedCount
  }
}

/**
 * Opens and migrates exactly one captured user/project database, then binds its
 * framework sync client to that same immutable handle.
 * @param {{migrations?: MigrationContext, projectId: unknown, routingEpoch: unknown, userId: unknown, configuration?: Configuration}} args Inputs.
 * @returns {Promise<OpenProjectReplica>} Captured tenant replica.
 * @rejects {Error} When identity, schema initialization, or sync bootstrap fails.
 */
export async function openProjectReplica({migrations = projectReplicaMigrations(), projectId, routingEpoch, userId, configuration = Configuration.current()}) {
  const identity = projectReplicaIdentity({projectId, userId})
  const epoch = validRoutingEpoch(routingEpoch)

  return await withSyncClientLifecycle(async () => {
    const handle = Tenant.handle(identity, configuration)
    const databaseIdentity = handle.databaseIdentity(PROJECT_REPLICA_DATABASE_IDENTIFIER)
    const previousReplica = activeSyncClients.get(databaseIdentity)

    if (previousReplica) await retireActiveProjectReplica(previousReplica, {markSealed: true})
    await prepareProjectReplicaCapacity({configuration, handle})
    await handle.initialize({
      databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER,
      migrations,
      schemaGeneration: PROJECT_REPLICA_SCHEMA_GENERATION
    })

    /** @type {ProjectSyncClient | undefined} */
    let syncClient
    /** @type {SyncCoordinator | undefined} */
    let coordinator
    /** @type {LocalMutationLog | undefined} */
    let appOfflineMutationLog

    try {
      const pendingMutationCount = await projectReplicaPendingMutationCountForHandle(handle)
      const {flushPendingWrites, mutationLog} = await createProjectReplicaMutationLog({handle})
      const actorDeviceId = await projectSyncDeviceId()

      const {flushPendingWrites: flushAppQueueWrites, mutationLog: appQueueLog} = await createAppOfflineMutationLog({handle})
      appOfflineMutationLog = appQueueLog
      registerAppOfflineMutationLog({flushPendingWrites: flushAppQueueWrites, identity, mutationLog: appQueueLog})

      try {
        await bindOfflineSyncProvider({actorDeviceId, actorUserId: identity.userId, mutationLog: appQueueLog})
      } catch (bindError) {
        // A failed grant acquisition must not block replica open; the provider
        // stays disabled and a later coordinator tick re-acquires on demand.
        reportProjectSyncError(bindError)
      }

      configureProjectSyncDeclarations({actorDeviceId, actorUserId: identity.userId, mutationLog})

      syncClient = new ProjectSyncClient({
        configuration,
        databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER,
        flushAppOfflineQueue: async () => await flushOfflineSyncQueue({mutationLog: appQueueLog}),
        flushMutationLog: flushPendingWrites,
        requestContext: {
          pendingMutationCount,
          projectId: identity.projectId,
          protocolVersion: 2,
          routingEpoch: epoch
        },
        tenantHandle: handle
      })
      coordinator = createProjectSyncCoordinator({beforeStatusSave: flushPendingWrites, handle, identity, syncClient})
      const activeReplica = Object.freeze({appOfflineMutationLog: appQueueLog, coordinator, handle, identity, migrations, routingEpoch: epoch, syncClient})

      await coordinator.start()
      activeSyncClients.set(databaseIdentity, activeReplica)
      await upsertRegistryEntry({handle, identity, routingEpoch: epoch, state: "active"})

      return Object.freeze({coordinator, handle, identity, syncClient})
    } catch (error) {
      /** @type {unknown[]} */
      const cleanupErrors = []

      activeSyncClients.delete(databaseIdentity)
      if (appOfflineMutationLog) {
        releaseOfflineSyncProviderBinding(appOfflineMutationLog)
        unregisterAppOfflineMutationLog({identity, mutationLog: appOfflineMutationLog})
      }
      if (coordinator) {
        try {
          await coordinator.stop()
        } catch (cleanupError) {
          cleanupErrors.push(cleanupError)
        }
      } else if (syncClient) {
        try {
          await syncClient.stop()
        } catch (cleanupError) {
          cleanupErrors.push(cleanupError)
        }
      }
      try {
        await handle.close({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER, flush: true})
      } catch (cleanupError) {
        cleanupErrors.push(cleanupError)
      }
      if (cleanupErrors.length > 0) throw new AggregateError([error, ...cleanupErrors], "Project replica startup and cleanup failed")
      throw error
    }
  })
}

/**
 * Stops project sync, durably flushes, closes, and seals the catalog row.
 * @param {OpenProjectReplica} replica Open replica ownership bundle.
 * @returns {Promise<void>} Completion after durable close.
 */
export async function closeProjectReplica(replica) {
  await withSyncClientLifecycle(async () => {
    const databaseIdentity = replica.handle.databaseIdentity(PROJECT_REPLICA_DATABASE_IDENTIFIER)
    const activeReplica = activeSyncClients.get(databaseIdentity)

    if (activeReplica && activeReplica.syncClient !== replica.syncClient) return
    if (activeReplica) {
      await retireActiveProjectReplica(activeReplica, {markSealed: true})
      return
    }

    await replica.coordinator.stop()
    await replica.handle.close({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER, flush: true})
    await markRegistryState(replica.identity, "sealed")
  })
}

/**
 * Refuses sign-out/storage sealing if any retained project has pending or failed
 * intent. Clean replicas are flushed and closed but deliberately retained.
 * @param {{migrations?: MigrationContext, restoreOnFailure?: boolean, userId: unknown, configuration?: Configuration}} args Seal inputs.
 * @returns {Promise<void>} Completion after every clean replica is sealed.
 * @rejects {ProjectReplicaUnsyncedError | Error} When work is unsynced or a lifecycle action fails.
 */
export async function sealProjectReplicasForUser({migrations, restoreOnFailure = false, userId, configuration = Configuration.current()}) {
  const canonicalUserId = projectReplicaIdentity({projectId: "00000000-0000-4000-8000-000000000000", userId}).userId

  await withSyncClientLifecycle(async () => {
    const entries = await ProjectReplicaRegistryEntry.where({userId: canonicalUserId}).toArray()
    entries.sort((left, right) => {
      const leftIdentity = projectReplicaIdentity({projectId: left.projectId(), userId: canonicalUserId})
      const rightIdentity = projectReplicaIdentity({projectId: right.projectId(), userId: canonicalUserId})
      const leftActive = activeSyncClients.has(Tenant.handle(leftIdentity, configuration).databaseIdentity(PROJECT_REPLICA_DATABASE_IDENTIFIER))
      const rightActive = activeSyncClients.has(Tenant.handle(rightIdentity, configuration).databaseIdentity(PROJECT_REPLICA_DATABASE_IDENTIFIER))

      return Number(rightActive) - Number(leftActive) || leftIdentity.projectId.localeCompare(rightIdentity.projectId)
    })
    if (entries.length > 0 && !migrations) migrations = projectReplicaMigrations()
    let unsyncedCount = 0
    /** @type {unknown[]} */
    const lifecycleErrors = []
    /** @type {ActiveProjectReplica[]} */
    const activeReplicasToRestore = []

    for (const entry of entries) {
      const identity = projectReplicaIdentity({projectId: entry.projectId(), userId: canonicalUserId})
      const handle = Tenant.handle(identity, configuration)
      const databaseIdentity = handle.databaseIdentity(PROJECT_REPLICA_DATABASE_IDENTIFIER)
      const activeReplica = activeSyncClients.get(databaseIdentity)

      if (activeReplica) {
        if (restoreOnFailure) activeReplicasToRestore.push(activeReplica)
        try {
          await activeReplica.coordinator.stop()
        } catch (error) {
          lifecycleErrors.push(error)
        }
        activeSyncClients.delete(databaseIdentity)
      }

      try {
        await prepareProjectReplicaCapacity({configuration, handle})
        await handle.initialize({
          databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER,
          migrations: /** @type {MigrationContext} */ (migrations),
          schemaGeneration: PROJECT_REPLICA_SCHEMA_GENERATION
        })
        unsyncedCount += await projectReplicaPendingMutationCountForHandle(handle)
      } catch (error) {
        lifecycleErrors.push(error)
      } finally {
        let closed = false

        try {
          await handle.close({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER, flush: true})
          closed = true
        } catch (error) {
          lifecycleErrors.push(error)
        }

        if (closed) {
          // The handle is closed: release any provider binding armed against
          // this replica's app queue log so later saves cannot queue into a
          // closed SQLite handle.
          if (activeReplica?.appOfflineMutationLog) releaseOfflineSyncProviderBinding(activeReplica.appOfflineMutationLog)
          try {
            entry.setState("sealed")
            await entry.save()
          } catch (error) {
            lifecycleErrors.push(error)
          }
        }
      }
    }

    if (unsyncedCount > 0) lifecycleErrors.push(new ProjectReplicaUnsyncedError(unsyncedCount))
    if (restoreOnFailure && lifecycleErrors.length > 0) {
      for (const replica of activeReplicasToRestore) {
        try {
          await restoreActiveProjectReplica(replica)
        } catch (error) {
          lifecycleErrors.push(error)
        }
      }
    }
    if (lifecycleErrors.length === 1) throw lifecycleErrors[0]
    if (lifecycleErrors.length > 1) throw new AggregateError(lifecycleErrors, "Failed to inspect and seal every retained project replica")
  })
}

/**
 * Restores a coordinator stopped only for a refused guarded auth transition.
 * @param {ActiveProjectReplica} replica Previously active ownership bundle.
 * @returns {Promise<void>} Completion after local initialization and coordinator ownership.
 */
async function restoreActiveProjectReplica(replica) {
  const {coordinator, handle, identity, migrations, routingEpoch} = replica
  const databaseIdentity = handle.databaseIdentity(PROJECT_REPLICA_DATABASE_IDENTIFIER)

  await handle.initialize({
    databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER,
    migrations,
    schemaGeneration: PROJECT_REPLICA_SCHEMA_GENERATION
  })
  await coordinator.start()
  activeSyncClients.set(databaseIdentity, replica)
  await upsertRegistryEntry({handle, identity, routingEpoch, state: "active"})
}

/**
 * Explicit test cleanup; production sign-out never deletes project storage.
 * @param {{migrations?: MigrationContext, configuration?: Configuration}} [options] Exact cleanup inputs.
 * @returns {Promise<void>} Completion after registered test databases are deleted.
 */
export async function resetProjectReplicasForTests({migrations = projectReplicaMigrations(), configuration = Configuration.current()} = {}) {
  await withSyncClientLifecycle(async () => {
    for (const replica of activeSyncClients.values()) await replica.coordinator.stop()
    activeSyncClients.clear()
    resetProjectReplicaTransportHandlesForTests()

    const entries = await ProjectReplicaRegistryEntry.toArray()
    for (const entry of entries) {
      const handle = Tenant.handle(projectReplicaIdentity({projectId: entry.projectId(), userId: entry.userId()}), configuration)
      await prepareProjectReplicaCapacity({configuration, handle})
      await handle.initialize({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER, migrations, schemaGeneration: PROJECT_REPLICA_SCHEMA_GENERATION})
      await handle.delete({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER})
    }
    await ProjectReplicaRegistryEntry.destroyAll()
  })
}

/**
 * Stops a client before its physical handle can close. A close refusal restores
 * the same client/transport binding, so the active map never points at a closed
 * or half-retired handle.
 * @param {ActiveProjectReplica} replica Active replica ownership.
 * @param {{markSealed: boolean}} options Registry policy.
 * @returns {Promise<void>} Completion after safe retirement.
 * @rejects {Error} When the handle is pinned or client/handle retirement fails.
 */
async function retireActiveProjectReplica(replica, {markSealed}) {
  const {appOfflineMutationLog, coordinator, handle, identity} = replica
  const databaseIdentity = handle.databaseIdentity(PROJECT_REPLICA_DATABASE_IDENTIFIER)

  // Release the offline provider binding before the handle closes: the bound
  // mutation log lives on this replica's SQLite handle, and queueing into it
  // after close throws inside unrelated frontend-model saves (catalog cursor
  // writes surfaced as `sync-failed`).
  if (appOfflineMutationLog) releaseOfflineSyncProviderBinding(appOfflineMutationLog)

  await coordinator.stop()
  const lifecycle = handle.inspect({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER})
  if (lifecycle.pinCount > 0) {
    await coordinator.start()
    throw new Error("Cannot close a pinned frontend tenant SQLite handle")
  }
  try {
    await handle.flush({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER})
    await handle.close({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER, flush: true})
  } catch (closeError) {
    try {
      await coordinator.start()
    } catch (restoreError) {
      throw new AggregateError([closeError, restoreError], "Project replica close failed and its coordinator could not be restored")
    }
    throw closeError
  }

  if (activeSyncClients.get(databaseIdentity) === replica) activeSyncClients.delete(databaseIdentity)
  if (markSealed) await markRegistryState(identity, "sealed")
}

/**
 * Captures this client's immutable actor metadata and shared tenant log into
 * each project-domain declaration before SyncClient derives its private config.
 * @param {{actorDeviceId: string, actorUserId: string, mutationLog: import("velocious/build/src/sync/local-mutation-log.js").default}} args Tracking inputs.
 */
function configureProjectSyncDeclarations({actorDeviceId, actorUserId, mutationLog}) {
  const conflictTracking = {
    actorDeviceId,
    actorUserId,
    clientMutationId: syncUuid,
    mutationLog,
    offlineGrantId: "awesome-tasks-foreground-v2",
    policyHash: "awesome-tasks-project-sync-v2",
    versionAttribute: "updatedAt"
  }
  const declaration = projectReplicaSyncDeclaration({conflictTracking})

  for (const modelClass of [Board, BoardColumn, Comment, TaskAssignedUser, TaskBoard, TaskType, Task, Timelog]) {
    modelClass.sync = declaration
  }
}

/**
 * Frees one framework handle slot without allowing automatic LRU to close a
 * database that still owns an active SyncClient. Unmanaged clean handles remain
 * eligible for Velocious's own eviction; pinned/in-use active handles fail safe.
 * @param {{configuration: Configuration, handle: TenantHandle}} args Admission inputs.
 * @returns {Promise<void>} Completion once admission cannot orphan an active client.
 * @rejects {Error} When retiring an eligible active replica fails unexpectedly.
 */
async function prepareProjectReplicaCapacity({configuration, handle}) {
  if (handle.inspect({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}).state === "open") return

  const targetIdentity = handle.databaseIdentity(PROJECT_REPLICA_DATABASE_IDENTIFIER)
  let diagnostics = configuration.inspectFrontendTenantSqliteHandles()

  if (diagnostics.openCount < diagnostics.maxOpenHandles) return

  const activeReplicasByLeastRecentUse = [...activeSyncClients].sort((left, right) => {
    const leftLifecycle = left[1].handle.inspect({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER})
    const rightLifecycle = right[1].handle.inspect({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER})

    return leftLifecycle.lastUsed - rightLifecycle.lastUsed
  })

  for (const [databaseIdentity, replica] of activeReplicasByLeastRecentUse) {
    if (databaseIdentity === targetIdentity) continue
    if (replica.handle.inspect({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}).dirty) continue

    try {
      await retireActiveProjectReplica(replica, {markSealed: true})
    } catch (error) {
      const lifecycle = replica.handle.inspect({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER})

      if (!lifecycle.dirty && lifecycle.pinCount === 0) throw error
    }

    diagnostics = configuration.inspectFrontendTenantSqliteHandles()
    if (diagnostics.openCount < diagnostics.maxOpenHandles) return
  }
}

/**
 * Serializes app-owned SyncClient admission and retirement without replacing
 * Velocious's physical database lifecycle or eviction policy.
 * @template T Callback result type.
 * @param {() => Promise<T>} callback Lifecycle work.
 * @returns {Promise<T>} Callback result.
 */
async function withSyncClientLifecycle(callback) {
  const previous = syncClientLifecycle
  /**
   * Releases the next serialized lifecycle caller.
   * @type {() => void}
   */
  let release = () => {}

  syncClientLifecycle = new Promise((resolve) => { release = resolve })
  await previous
  try {
    return await callback()
  } finally {
    release()
  }
}

/**
 * Persists the catalog row matching an opened physical database.
 * @param {{handle: TenantHandle, identity: ProjectReplicaIdentity, routingEpoch: number, state: string}} args Registry inputs.
 * @returns {Promise<void>} Completion after the catalog row is durable.
 */
async function upsertRegistryEntry({handle, identity, routingEpoch, state}) {
  const entry = await ProjectReplicaRegistryEntry.findOrInitializeBy({projectId: identity.projectId, userId: identity.userId})
  entry.assign({
    lastOpenedAt: new Date(),
    physicalName: handle.databaseConfiguration(PROJECT_REPLICA_DATABASE_IDENTIFIER).name,
    routingEpoch,
    state
  })
  await entry.save()
}

/**
 * Marks an existing registry row with its lifecycle state.
 * @param {ProjectReplicaIdentity} identity Replica identity.
 * @param {string} state State name.
 * @returns {Promise<void>} Completion after the row is saved.
 * @rejects {Error} When the registry row disappeared.
 */
async function markRegistryState(identity, state) {
  const entry = await ProjectReplicaRegistryEntry.findBy({projectId: identity.projectId, userId: identity.userId})
  if (!entry) throw new Error("Project replica registry entry disappeared while closing")
  entry.setState(state)
  await entry.save()
}

/**
 * Validates a backend routing epoch.
 * @param {unknown} value Candidate epoch.
 * @returns {number} Positive safe integer epoch.
 * @throws {Error} When the value cannot safely route a project.
 */
function validRoutingEpoch(value) {
  if (!Number.isSafeInteger(value) || Number(value) < 1) throw new Error("Project replica requires a positive routing epoch")
  return Number(value)
}

/**
 * Loads bundled project-replica migrations in Metro runtimes.
 * @returns {MigrationContext} Metro migration context.
 */
function projectReplicaMigrations() {
  // @ts-expect-error — Metro-specific require.context is unavailable to Node type definitions.
  return require.context("./project-replica-migrations", true, /^\.\/(.+)\.js$/)
}
